import React, { useEffect } from "react";
import { Alert, AlertProps, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, } from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId, GridRowModesModel, GridRowsProp, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import {getActiveAccount, getToken, isUser, useAuthentication, useLogin} from "../../authConfig";
import { useMsal } from "@azure/msal-react";
import { Delete, UploadFile } from "@mui/icons-material";
import Snackbar from "@mui/material/Snackbar";
import { deleteDocument, getUploadedDocs, uploadDocumentFiles, UploadedDocs, UploadedDocsResponse } from "../../api";
import {ADMIN_ROLES, NO_ACCESS_ROLE} from "../../constants";
import { FsnUser } from "@fusion/components/common";

interface EditToolbarProps {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (newModel: (oldModel: GridRowModesModel) => GridRowModesModel) => void;
    fileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    refreshTable: (page: number, size: number) => void;
    handleOpen: () => void;
    selectedDocRow: GridRowId[]; 
}

function EditToolbar(props: EditToolbarProps) {
    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const { fileUpload, handleOpen, selectedDocRow } = props;
    const [loading, setLoading] = React.useState<boolean>(true);
    const client = useLogin ? useMsal().instance : undefined;
    const [snackbar, setSnackbar] = React.useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const { isAdmin } = useAuthentication();

    const refreshTable = async (page: number, size: number) => {
        try {
            const token = client ? await getToken(client) : undefined;
            const response = await getUploadedDocs(token, page, size);
            const data = (await response.json()) as UploadedDocsResponse;
            const uploaded_docs = data.uploaded_docs;
            setRows(uploaded_docs.map((row: UploadedDocs) => ({ ...row, id: row.file_name, timestamp: new Date(row.uploaded_dt) })));
        }
        catch (error) {
            setSnackbar({ children: "Error fetching data", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <GridToolbarContainer>
            <GridToolbarFilterButton />
            <GridToolbarExport csvOptions={{
                fileName: `Chat GPSC Document List ${new Date().toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                }).replaceAll(/[,:_]/g, "").replaceAll("/", "-")}`
            }}/>
            {
                isAdmin() && (
                    <>
                        <Button component="label" startIcon={<UploadFile />}>
                            Upload Document <input onChange={fileUpload} type="file" hidden accept=".pdf" multiple />
                        </Button>
                        <GridActionsCellItem
                            icon={<Delete />}
                            label="Delete"
                            color="inherit"
                            onClick={handleOpen}
                            disabled={selectedDocRow.length === 0}
                        />
                    </>
                )
            }
        </GridToolbarContainer>
    )
}

const DocumentTable = () => {
    const [page, setPage] = React.useState<number>(1);
    const [size, setSize] = React.useState<number>(20);
    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const [snackbar, setSnackbar] = React.useState<Pick<AlertProps, "children" | "severity"> | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const client = useLogin ? useMsal().instance : undefined;
    const [selectedDocRow, setSelectedDocRow] = React.useState<GridRowId[]>([]);
    const [open, setOpen] = React.useState<boolean>(false);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); }
    const { isAdmin } = useAuthentication();

    const refreshTable = async (page: number, size: number) => {
        try {
            const token = client ? await getToken(client) : undefined;
            const response = await getUploadedDocs(token, page, size);
            const data = (await response.json()) as UploadedDocsResponse;
            const uploadedDocs = data.uploaded_docs;
            setRows(uploadedDocs.map((row: UploadedDocs, index: number) => ({ ...row, id: row["file_name"], timestamp: new Date(row["uploaded_dt"]) })));
        }
        catch (error) {
            setSnackbar({ children: "Error fetching documents", severity: "error" });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setLoading(true);
        const files = e.target.files;

        if (!files || files.length === 0) {
            return;
        }
        try {
            if (isAdmin()) {
                const token = client ? await getToken(client) : undefined;
                // Check total file size BEFORE uploading (prevents connection abort)
                const MAX_CONTENT_LENGTH = 150 * 1024 * 1024;
                let totalUploadFilesSize: number = 0;
                for (const file of files) {
                    totalUploadFilesSize += file.size;
                }

                if (totalUploadFilesSize > MAX_CONTENT_LENGTH) {
                    const sizeInMB = (totalUploadFilesSize / 1024 / 1024).toFixed(2);
                    setSnackbar({
                        children: `Total upload file(s) size (${sizeInMB}MB) exceeds the maximum limit of 150MB. Please reduce file size or upload fewer files.`,
                        severity: "error"
                    });
                    e.target.value = '';
                    setLoading(false);
                    return;
                }

                const response = await uploadDocumentFiles(token, Array.from(files))
                if (response.status === 200) {
                    const data = await response.json();
                    await refreshTable(page, size);
                    
                    // All PDF Upload
                    if (data.message.includes('Upload successful: All PDF documents have been uploaded')) {
                        setSnackbar({
                            children: data.message,
                            severity: "success"
                        });
                    }
                    // No PDF Upload
                    else if (data.message.includes('None of the files were in PDF format')) {
                        setSnackbar({
                            children: data.message,
                            severity: "error"
                        });
                    }
                    // Mixed File Upload
                    else {
                        setSnackbar({
                            children: `${data.message} ${data.filenames.join(", ")}`,
                            severity: "success"
                        });
                    }
                }
                else {
                    setSnackbar({ children: "Error uploading documents", severity: "error" })
                }
            }
            else {
                setSnackbar({ children: "You must have admin permissions to upload documents.", severity: "error" })
            }
        }
        catch (error) {
            setSnackbar({ children: "Error uploading documents", severity: "error" })
        } finally {
            e.target.value = '';
            setLoading(false)
        }
    };

    const handleDeleteSelected = (ids: GridRowId[]) => async () => {
        handleClose();
        setLoading(true);
        try {
            if (isAdmin()) {
                const token = client ? await getToken(client) : undefined;
                const filenames = ids.map(id => id.toString());
                const response = await deleteDocument(token, filenames);
                if (response.status !== 200) {
                    throw new Error("Failed to delete documents");
                }
                setSnackbar({ children: "Documents deleted successfully" });
                setRows(rows.filter(row => !ids.includes(row.id)));
            }
            else {
                setSnackbar({ children: "Error deleting documents", severity: "error" });
            }
        } catch (error) {
            setSnackbar({children: "Error deleting documents", severity: "error"});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        refreshTable(page, size)
    }, [page, size])

    const handleCloseSnackbar = () => setSnackbar(null);

    const columns: GridColDef[] = [
        {
            field: "file_name",
            headerName: "Document",
            width: 500,
            editable: false
        },
        {
            field: "uploaded_dt",
            headerName: "Upload Date",
            width: 275,
            editable: false
        },
        {
            field: "uploaded_by",
            headerName: "Uploaded By",
            width: 275,
            editable: false
        },
        {
            field: "domain_name",
            headerName: "Domain",
            width: 300,
            editable: false
        }
    ];
    return (
        <Box
            sx={{
                height: 800,
                width: "100%",
                "& .actions": {
                    color: "text.secondary"
                },
                "& .textPrimary": {
                    color: "text.primary"
                },
                '& .MuiDataGrid-cell': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'left',
                }
            }}
        >
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" sx={{ fontSize: "1rem" }}>
                    Confirmation
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        The selected file(s) will be removed. Do you wish to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleDeleteSelected(selectedDocRow)} autoFocus>Ok</Button>
                </DialogActions>
            </Dialog>
            <DataGrid
                rows={rows}
                columns={columns}
                editMode="row"
                checkboxSelection
                disableRowSelectionOnClick
                slots={{
                    toolbar: () =>
                        <EditToolbar
                            setRows={setRows}
                            setRowModesModel={setRowModesModel}
                            fileUpload={fileUpload}
                            refreshTable={refreshTable}
                            handleOpen={handleOpen}
                            selectedDocRow={selectedDocRow}
                        />
                }}
                onRowSelectionModelChange={(newSelection) => {
                    setSelectedDocRow([...newSelection]);
                }}
                loading={loading}
                getRowHeight={() => 'auto'}
            />
            {!!snackbar && (
                <Snackbar open anchorOrigin={{ vertical: "bottom", horizontal: "center" }} onClose={handleCloseSnackbar} autoHideDuration={6000}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </Box>
    )
}
export default DocumentTable;